import React, { useEffect } from 'react';
import { useI18n } from '../i18n/i18nContext';
import Hero from '../components/Hero';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useServiceStore } from '../stores/serviceStore';
import LocalizedLink from '../components/LocalizedLink';
import { getTranslatedField } from '../utils/translationUtils';
import NewsletterSubscription from '../components/NewsletterSubscription';
import ContentPageRenderer from '../components/ContentPageRenderer';
import SEO from '../components/SEO';
import CarouselGallery from '../components/CarouselGallery';
import { usePackageStore } from 'src/stores/packageStore';
import { Package, Service } from '@shared/types';

const HomePage: React.FC = () => {
  const { t, language } = useI18n();
  useDocumentTitle(t.pageTitles.home);
  const { services, loadServices } = useServiceStore();
  const { packages, loadPackages } = usePackageStore();

  useEffect(() => {
    loadServices(language);
  }, [loadServices, language]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  return (
    <div className="flex flex-col">
      <SEO description={t.seo.homeDescription} />
      <Hero />
      <ContentPageRenderer
        slug="home-intro"
        className="container m-8 mx-auto max-w-6xl px-4 prose-h2:text-[29px] prose-h2:font-bold prose-h2:text-black prose-p:text-[20px] prose-p:text-black prose-strong:text-[23px] prose-strong:font-semibold prose-strong:text-black"
      />

      <main className="flex-grow">
        <div id="packages" className="container mx-auto max-w-6xl bg-gray-100 px-4 py-16">
          <h2 className="mb-10 text-center text-[34px] font-bold text-black md:text-[42px]">
            {t.ourPackages}
          </h2>
          <div className="grid grid-cols-1 gap-x-4 gap-y-8 md:grid-cols-2 lg:grid-cols-3">
            {packages.map((pkg: Package) => (
              <LocalizedLink
                to={`/book/package/${pkg.id}`}
                key={pkg.id}
                className="group flex h-full flex-col rounded-2xl border border-slate-100 bg-gray-50 transition-all duration-300 hover:scale-[1.02]"
              >
                <div className="relative overflow-hidden rounded-3xl">
                  <img
                    className="h-48 w-full object-cover"
                    src={pkg?.image_url}
                    alt={getTranslatedField(pkg, 'name', language)}
                  />
                  <div className="absolute inset-0 bg-black/35" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center text-white">
                    <p className="break-words text-[18px] font-bold leading-tight md:text-[18px]">
                      {getTranslatedField(pkg, 'name', language)}
                    </p>
                    <p className="mt-1 text-[18px] font-bold leading-tight md:text-[18px]">{`(${pkg.duration_minutes} ${t.min})`}</p>
                  </div>
                </div>
                <div className="flex min-w-0 flex-1 flex-col px-6 py-6 text-black">
                  <h3 className="mb-5 text-[18px] font-bold md:text-[18px]">{t.packageIncludes}</h3>
                  <div className="space-y-6">
                    {pkg.services?.map((service: Service) => (
                      <div key={service.id} className="min-w-0">
                        <p className="break-words text-[18px] font-bold leading-tight md:text-[18px]">
                          {getTranslatedField(service, 'name', language)}
                        </p>
                        <p className="mt-1 break-words text-[18px] leading-snug md:text-[18px]">
                          {getTranslatedField(service, 'description', language)}
                        </p>
                      </div>
                    ))}
                  </div>
                  {getTranslatedField(pkg, 'additional_info', language) && (
                    <div
                      className="mb-4 mt-8"
                      dangerouslySetInnerHTML={{
                        __html: getTranslatedField(pkg, 'additional_info', language) || '',
                      }}
                    />
                  )}

                  <span className="mt-auto inline-block pt-8 text-[18px] font-semibold underline md:pt-10 md:text-[18px]">
                    {t.bookPackage} &rarr;
                  </span>
                </div>
              </LocalizedLink>
            ))}
          </div>
        </div>

        <div id="services" className="container mx-auto max-w-6xl px-4 py-16">
          <h2 className="mb-10 text-center text-[34px] font-bold text-black md:text-[42px]">
            {t.ourServices}
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => (
              <LocalizedLink
                to={`/services/${service.slug || ''}`}
                key={service.id}
                className="group flex h-full flex-col rounded-2xl transition-all duration-300 hover:scale-[1.02]"
              >
                <div className="relative overflow-hidden rounded-3xl">
                  <img
                    className="h-48 w-full object-cover"
                    src={service.image || `/images/services/${service.slug}.webp`}
                    alt={getTranslatedField(service, 'name', language)}
                  />
                  <div className="absolute inset-0 bg-black/35" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center text-white">
                    <p className="break-words text-[20px] font-bold leading-tight underline md:text-[22px]">
                      {getTranslatedField(service, 'name', language)}
                    </p>
                  </div>
                </div>
              </LocalizedLink>
            ))}
          </div>
        </div>
      </main>

      <NewsletterSubscription />
      <CarouselGallery />
    </div>
  );
};

export default HomePage;
